// src/components/AiDashboard/AiMyNutritionStatus/AiUser/AiUserInputNutritionData.jsx

import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';

import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, Timestamp, FieldPath } from 'firebase/firestore';
import { auth, db } from '../../../firebase/firebaseConfig';
import { createGeminiNutritionServiceFromFirestore } from './AiUser/AiUserInputSeparator/AiMealFirebaseConfig';
import nnr2023Data from './nnr2023-data';
import { LocalDateIso, LocalDateRolloverScheduler } from './AiUser/LocalDateIso';

import { aiUserVitamins, vitaminsChipText } from './AiUserVitamins';
import { aiUserMinerals, mineralsChipText } from './AiUserMinerals';
import {
  buildAiUserVitaminCardsFromNutritionCalculateSnapshot,
  buildAiUserMineralCardsFromNutritionCalculateSnapshot,
  buildAiUserVmHeadlineChip,
} from '../../nutrition/vitamins/aiUserVmSummary.ts';
import {
  uiConfig,
  sizeToPadding,
  routes,
  labels,
  createMealCardsFromLastNutrition,
  createNutritionFactsFromLastNutrition,
  buildDailyValueReference,
  aiNutritionViewModelFactory,
} from './AiUserFoodFacts';
import { buildAiUserMacrosFromNutritionCalculateSnapshot } from './AiUserMacros';
import { buildDynamicSuggestedLog } from './AiUserLogs';
import {
  buildNutritionTargetsSettingsPreview,
  buildUseDailyPreviewFromNnr2023,
} from './AiUser/AiUserSettings/nutritionTargetsSettings.ts';
import { buildNutritionTargetsCalculationsPreview } from './AiUser/AiUserSettings/nutritionTargetsCalculations.ts';

import {
  buildBmiNodeFromSnapshots,
  BmiInsight,
  buildNutrientPrioritySummary,
} from '../../nutrition/fitnessbodycomp/nutritionFitnessBodyComp';

import {
  createAdjustableMealFromNutrition,
  incrementItemPortion,
  decrementItemPortion,
  applyAdjustmentsToNutritionJson,
} from './AiUser/AiUserInputSeparator/AiMealPortionAdjuster';

function determineLifeStage(userProfile) {
  if (!userProfile) return null;

  const { ageYears, sex } = userProfile;
  if (!ageYears || !sex) return null;

  const lifeStage = nnr2023Data.lifeStages.find((stage) => {
    if (stage.condition) return false;

    const { min, max } = stage.age_years || {};
    if (min !== undefined && ageYears < min) return false;
    if (max !== undefined && ageYears > max) return false;
    if (stage.id.includes('male') && sex !== 'male') return false;
    if (stage.id.includes('female') && sex !== 'female') return false;

    return true;
  });

  return lifeStage;
}

function buildNutritionTargetsFromProfile(userProfile) {
  const lifeStage = determineLifeStage(userProfile);
  if (!lifeStage) return null;

  return {
    source: 'NNR2023',
    version: nnr2023Data.meta.version,
    generatedAtIso: new Date().toISOString(),
    profile: {
      ageYears: userProfile.ageYears,
      sex: userProfile.sex,
    },
    lifeStageId: lifeStage.id,
    lifeStageLabel: lifeStage.label,
    lifeStage: lifeStage,
  };
}

const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const randomBuffer =
      (crypto?.getRandomValues?.(new Uint8Array(1))[0] ??
        Math.random() * 16) | 0;
    const value =
      character === 'x'
        ? randomBuffer & 0xf
        : (randomBuffer & 0x3) | 0x8;
    return value.toString(16);
  });

const stableStringify = (object) => {
  const sortRecursively = (value) => {
    if (Array.isArray(value)) return value.map(sortRecursively);
    if (value && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((accumulator, key) => {
          accumulator[key] = sortRecursively(value[key]);
          return accumulator;
        }, {});
    }
    return value;
  };
  return JSON.stringify(sortRecursively(object));
};

const hash32 = (input) => {
  let hash = 2166136261;
  const stringValue = String(input ?? '');
  for (let index = 0; index < stringValue.length; index++) {
    hash ^= stringValue.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
};

const sleep = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));
const jitter = (baseMilliseconds) =>
  Math.floor(baseMilliseconds * (0.5 + Math.random()));

class IdempotencyStore {
  constructor(ttlMilliseconds = 10 * 60 * 1000) {
    this.ttlMilliseconds = ttlMilliseconds;
    this.map = new Map();
  }
  tryStart(key) {
    const now = Date.now();
    const found = this.map.get(key);
    if (found && now - found.at < this.ttlMilliseconds) return false;
    this.map.set(key, { at: now, done: false });
    return true;
  }
  markComplete(key) {
    const entry = this.map.get(key);
    if (entry) this.map.set(key, { ...entry, done: true });
  }
  sweep() {
    const now = Date.now();
    for (const [mapKey, value] of this.map.entries()) {
      if (now - value.at >= this.ttlMilliseconds) {
        this.map.delete(mapKey);
      }
    }
  }
}

class CircuitBreaker {
  constructor({ failureThreshold = 5, cooldownMs = 30_000 } = {}) {
    this.failureThreshold = failureThreshold;
    this.cooldownMs = cooldownMs;
    this.failures = 0;
    this.state = 'closed';
    this.until = 0;
  }
  canPass() {
    if (this.state === 'open' && Date.now() < this.until) return false;
    if (this.state === 'open' && Date.now() >= this.until) {
      this.state = 'half';
      return true;
    }
    return true;
  }
  recordSuccess() {
    this.failures = 0;
    this.state = 'closed';
  }
  recordFailure() {
    this.failures += 1;
    if (this.failures >= this.failureThreshold) {
      this.state = 'open';
      this.until = Date.now() + this.cooldownMs;
    }
  }
}

class SafeEventBus {
  constructor({
    serviceName,
    ingress,
    egressAllowlist,
    maxHopCount = 9,
    idempotencyTtlMs = 10 * 60 * 1000,
  }) {
    this.serviceName = serviceName;
    this.ingress = ingress;
    this.egressAllowlist = egressAllowlist;
    this.maxHopCount = Math.max(1, Math.min(maxHopCount, 10));
    this.idempotency = new IdempotencyStore(idempotencyTtlMs);
    this.lastChangeByEntity = new Map();
    this.breakers = new Map();
    this.deadLetterQueue = [];
  }

  breakerFor(destination) {
    if (!this.breakers.has(destination)) {
      this.breakers.set(
        destination,
        new CircuitBreaker({ failureThreshold: 4, cooldownMs: 20_000 })
      );
    }
    return this.breakers.get(destination);
  }

  _preflight(event, destination) {
    const allowed = this.egressAllowlist[this.ingress] || [];
    if (!allowed.includes(destination))
      return { ok: false, reason: 'egress_not_allowed' };
    if (destination === this.ingress)
      return { ok: false, reason: 'egress_same_as_ingress' };
    if (
      Array.isArray(event.visited) &&
      event.visited.includes(this.serviceName)
    )
      return { ok: false, reason: 'seen/self' };
    if (typeof event.hopCount === 'number' && event.hopCount >= this.maxHopCount)
      return { ok: false, reason: 'max_hops' };
    if (!event.id || !event.entityId || !event.changeHash)
      return { ok: false, reason: 'missing_identity' };
    return { ok: true };
  }

  _changeDetect(event) {
    const previous = this.lastChangeByEntity.get(event.entityId);
    if (previous && previous === event.changeHash) {
      return { ok: false, reason: 'no_op' };
    }
    this.lastChangeByEntity.set(event.entityId, event.changeHash);
    return { ok: true };
  }

  async publish(event, destination, options = { maxAttempts: 3 }) {
    if (!this.idempotency.tryStart(event.idempotencyKey)) {
      return { status: 'dropped', reason: 'duplicate' };
    }

    const changeResult = this._changeDetect(event);
    if (!changeResult.ok) {
      this.idempotency.markComplete(event.idempotencyKey);
      return { status: 'dropped', reason: changeResult.reason };
    }

    const preflightResult = this._preflight(event, destination);
    if (!preflightResult.ok) {
      this.idempotency.markComplete(event.idempotencyKey);
      return { status: 'dropped', reason: preflightResult.reason };
    }

    event.visited = Array.isArray(event.visited)
      ? [...event.visited, this.serviceName]
      : [this.serviceName];
    event.hopCount = (event.hopCount ?? 0) + 1;
    event.publishedAt = new Date().toISOString();

    const breaker = this.breakerFor(destination);
    if (!breaker.canPass()) {
      this.deadLetterQueue.push({
        event,
        destination,
        reason: 'breaker_open',
        at: Date.now(),
      });
      this.idempotency.markComplete(event.idempotencyKey);
      return { status: 'dlq', reason: 'breaker_open' };
    }

    let attempt = 0;
    const baseDelay = 400;
    const maxAttempts = Math.max(1, options.maxAttempts);

    while (attempt < maxAttempts) {
      attempt += 1;
      try {
        if (!/^meal\.log\./.test(event.type)) {
          throw new Error('event_type_not_allowed');
        }
        breaker.recordSuccess();
        this.idempotency.markComplete(event.idempotencyKey);
        return { status: 'published', destination, attempt };
      } catch (error) {
        breaker.recordFailure();
        if (attempt >= maxAttempts) {
          this.deadLetterQueue.push({
            event,
            destination,
            reason: 'exhausted',
            lastError: String(error?.message || error),
            at: Date.now(),
          });
          this.idempotency.markComplete(event.idempotencyKey);
          return {
            status: 'dlq',
            reason: 'exhausted',
            lastError: String(error?.message || error),
          };
        }
        await sleep(jitter(baseDelay * 2 ** (attempt - 1)));
      }
    }

    this.idempotency.markComplete(event.idempotencyKey);
    return { status: 'unknown' };
  }
}

const DEFAULT_VISIBILITY = {
  showVitamins: true,
  showConsumption: true,
  showSuggested: true,
  showWeightLossPlan: true,
};

class SectionVisibilitySession {
  constructor(defaults = DEFAULT_VISIBILITY) {
    this.defaults = { ...defaults };
    this.state = { ...defaults };
    this.listeners = new Set();
  }
  get() {
    return this.state;
  }
  set(patch) {
    this.state = { ...this.state, ...patch };
    this.emit();
  }
  reset() {
    this.state = { ...this.defaults };
    this.emit();
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  emit() {
    for (const listener of this.listeners) {
      try {
        listener(this.state);
      } catch {
        // Ignore listener errors.
      }
    }
  }
}

const sectionVisibilitySession = new SectionVisibilitySession();

let authWatcherStarted = false;
(function startAuthWatcherOnce() {
  if (authWatcherStarted) return;
  authWatcherStarted = true;
  try {
    onAuthStateChanged(auth, (user) => {
      if (!user) sectionVisibilitySession.reset();
    });
  } catch {
    // Ignore auth watcher errors
  }
})();

export function useSectionVisibility() {
  const [visibility, setVisibility] = useState(
    sectionVisibilitySession.get()
  );
  useEffect(() => {
    const unsubscribe = sectionVisibilitySession.subscribe(setVisibility);
    setVisibility(sectionVisibilitySession.get());
    return unsubscribe;
  }, []);
  const setShowVitamins = (value) =>
    sectionVisibilitySession.set({ showVitamins: !!value });
  const setShowConsumption = (value) =>
    sectionVisibilitySession.set({ showConsumption: !!value });
  const setShowSuggested = (value) =>
    sectionVisibilitySession.set({ showSuggested: !!value });
  const setShowWeightLossPlan = (value) =>
    sectionVisibilitySession.set({ showWeightLossPlan: !!value });
  return {
    ...visibility,
    setShowVitamins,
    setShowConsumption,
    setShowSuggested,
    setShowWeightLossPlan,
  };
}

export function resetSectionVisibility() {
  sectionVisibilitySession.reset();
}

const DataCtx = createContext(null);

export function AiUserInputNutritionDataProvider({ children }) {
  const serviceName = 'phn.ui.aiUserInput';
  const ingress = serviceName;
  const egressAllowlist = {
    [ingress]: ['phn.fn.meal.ingest', 'phn.queue.mealLogs'],
  };

  const busRef = useRef(null);
  if (!busRef.current) {
    busRef.current = new SafeEventBus({
      serviceName,
      ingress,
      egressAllowlist,
      maxHopCount: 9,
      idempotencyTtlMs: 15 * 60 * 1000,
    });
  }
  const bus = busRef.current;

  const [userRegion, setUserRegion] = useState('USA');
  const normalizeRegion = (region) => {
    const stringRegion = String(region || 'USA').trim().toUpperCase();
    if (
      stringRegion === 'EU' ||
      stringRegion === 'EUR' ||
      stringRegion === 'EUROPE'
    )
      return 'EU';
    if (stringRegion === 'CA' || stringRegion === 'CANADA') return 'USA';
    return 'USA';
  };

  const [userProfile, setUserProfile] = useState(null);
  const [nutritionTargets, setNutritionTargets] = useState(null);
  const [dailyValueReference, setDailyValueReference] = useState(null);
  const [nutritionCalculateSnapshotRange, setNutritionCalculateSnapshotRange] =
    useState(null);
  const [nutritionCalculateSnapshotToday, setNutritionCalculateSnapshotToday] =
    useState(null);

  const [activeDateIso, setActiveDateIso] = useState(() => LocalDateIso.today());

  useEffect(() => {
    const scheduler = new LocalDateRolloverScheduler(setActiveDateIso);
    scheduler.start();
    return () => scheduler.stop();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUserRegion('USA');
        setUserProfile(null);
        setNutritionTargets(null);
        setDailyValueReference(null);
        return;
      }
      try {
        const userRef = doc(db, 'users', user.uid);
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) {
          const userData = snapshot.data();
          setUserProfile(userData);

          const regionFromDoc = userData?.region;
          const normalizedRegion = normalizeRegion(regionFromDoc);
          setUserRegion(normalizedRegion);
        } else {
          setUserRegion('USA');
          setUserProfile(null);
        }

        try {
          let targetsRef = doc(db, 'users', user.uid, 'nutrition-targets', 'calculate');
          let targetsSnap = await getDoc(targetsRef);

          if (!targetsSnap.exists()) {
            targetsRef = doc(db, 'users', user.uid, 'nutrition-targets', 'current');
            targetsSnap = await getDoc(targetsRef);
          }

          if (!targetsSnap.exists()) {
            targetsRef = doc(db, 'users', user.uid, 'nnr2023-targets', 'current');
            targetsSnap = await getDoc(targetsRef);
          }

          if (targetsSnap.exists()) {
            const targets = targetsSnap.data();
            setNutritionTargets(targets);

            const dvRef = buildDailyValueReference(targets);
            setDailyValueReference(dvRef);

            aiNutritionViewModelFactory.setDailyValueReference(dvRef);
          } else {
            try {
              const userSnap = await getDoc(userRef);

              if (userSnap.exists()) {
                const userData = userSnap.data();
                const birthDateIso = userData.birthDateIso;
                const sex = userData.sex;

                if (birthDateIso && sex) {
                  const birthDate = new Date(birthDateIso);
                  const ageYears = Math.floor(
                    (Date.now() - birthDate.getTime()) /
                    (365.25 * 24 * 60 * 60 * 1000)
                  );

                  const profileForTargets = { ageYears, sex };
                  const targets = buildNutritionTargetsFromProfile(profileForTargets);

                  if (targets) {
                    setNutritionTargets(targets);

                    const dvRef = buildDailyValueReference(targets);
                    setDailyValueReference(dvRef);

                    aiNutritionViewModelFactory.setDailyValueReference(dvRef);
                  } else {
                    setNutritionTargets(null);
                    setDailyValueReference(null);
                  }
                } else {
                  setNutritionTargets(null);
                  setDailyValueReference(null);
                }
              } else {
                setNutritionTargets(null);
                setDailyValueReference(null);
              }
            } catch (profileError) {
              setNutritionTargets(null);
              setDailyValueReference(null);
            }
          }
        } catch (targetsError) {
          setNutritionTargets(null);
          setDailyValueReference(null);
        }
      } catch (error) {
        setUserRegion('USA');
        setUserProfile(null);
        setNutritionTargets(null);
        setDailyValueReference(null);
      }
    });
    return () => {
      try {
        unsubscribe && unsubscribe();
      } catch {
        // Ignore unsubscribe errors
      }
    };
  }, []);

  const geminiServiceRef = useRef(null);
  const ensureGeminiService = async () => {
    if (geminiServiceRef.current) return geminiServiceRef.current;
    try {
      const service = await createGeminiNutritionServiceFromFirestore({
        db,
        modelId: 'gemini-2.5-flash-lite',
        allowedRoutes: { nutrition_ingress: ['gemini_egress'] },
      });
      geminiServiceRef.current = service;
      return service;
    } catch (error) {
      geminiServiceRef.current = null;
      return null;
    }
  };

  const [latestMinimalJson, setLatestMinimalJson] = useState(null);
  const [latestNutritionJson, setLatestNutritionJson] = useState(null);

  const [mealAdjustState, setMealAdjustState] = useState(null);

  useEffect(() => {
    if (latestNutritionJson && typeof latestNutritionJson === 'object') {
      setMealAdjustState(
        createAdjustableMealFromNutrition(latestNutritionJson)
      );
    } else {
      setMealAdjustState(null);
    }
  }, [latestNutritionJson]);

  useEffect(() => {
    let cancelled = false;

    async function loadRecentNutritionCalculateSnapshot() {
      try {
        const user = auth.currentUser;
        if (!user || !userProfile) {
          if (!cancelled) setNutritionCalculateSnapshotRange(null);
          return;
        }

        const endIso = activeDateIso || LocalDateIso.today();
        const startIso = LocalDateIso.addDays(endIso, -6);

        if (!endIso || !startIso) {
          if (!cancelled) setNutritionCalculateSnapshotRange(null);
          return;
        }

        const dailyIntakeCollectionRef = collection(db, 'users', user.uid, 'dailyIntake');

        let querySnapshot = await getDocs(
          query(
            dailyIntakeCollectionRef,
            where('dateIso', '>=', startIso),
            where('dateIso', '<=', endIso),
          ),
        );

        if (querySnapshot.empty) {
          querySnapshot = await getDocs(
            query(
              dailyIntakeCollectionRef,
              where(FieldPath.documentId(), '>=', startIso),
              where(FieldPath.documentId(), '<=', endIso),
            ),
          );
        }

        const dailyIntakeByDate = {};

        querySnapshot.forEach((docSnapshot) => {
          const documentData = docSnapshot.data();
          const dateIsoFromDocument =
            typeof documentData?.dateIso === 'string' ? documentData.dateIso : docSnapshot.id;

          dailyIntakeByDate[dateIsoFromDocument] = documentData;
        });

        if (!dailyIntakeByDate[endIso]) {
          dailyIntakeByDate[endIso] = LocalDateIso.createBlankDailyIntakeDocument(endIso);
        }

        const targetsSettingsSnapshot = buildNutritionTargetsSettingsPreview({
          userProfile,
          dailyIntakeByDate,
        });

        const useDailySnapshot = buildUseDailyPreviewFromNnr2023({
          userProfile,
          dailyIntakeByDate,
        });

        const snapshot = buildNutritionTargetsCalculationsPreview({
          targetsSnapshot: targetsSettingsSnapshot,
          useDailySnapshot,
          activeDateIso: endIso,
          rangeKind: '7d',
          rangeStartDateIso: startIso,
          rangeEndDateIso: endIso,
        });

        if (!cancelled) {
          setNutritionCalculateSnapshotRange(snapshot);
        }
      } catch (error) {
        if (!cancelled) {
          setNutritionCalculateSnapshotRange(null);
        }
      }
    }

    if (userProfile) {
      loadRecentNutritionCalculateSnapshot();
    } else {
      setNutritionCalculateSnapshotRange(null);
    }

    return () => {
      cancelled = true;
    };
  }, [userProfile, activeDateIso]);

  useEffect(() => {
    let cancelled = false;

    async function loadTodayNutritionCalculateSnapshot() {
      try {
        const user = auth.currentUser;
        if (!user || !userProfile) {
          if (!cancelled) setNutritionCalculateSnapshotToday(null);
          return;
        }

        const endIso = activeDateIso || LocalDateIso.today();
        if (!endIso) {
          if (!cancelled) setNutritionCalculateSnapshotToday(null);
          return;
        }

        const dailyIntakeDocRef = doc(db, 'users', user.uid, 'dailyIntake', endIso);
        const docSnapshot = await getDoc(dailyIntakeDocRef);

        const dailyIntakeByDate = {
          [endIso]: docSnapshot.exists()
            ? docSnapshot.data()
            : LocalDateIso.createBlankDailyIntakeDocument(endIso),
        };

        const targetsSettingsSnapshot = buildNutritionTargetsSettingsPreview({
          userProfile,
          dailyIntakeByDate,
        });

        const useDailySnapshot = buildUseDailyPreviewFromNnr2023({
          userProfile,
          dailyIntakeByDate,
        });

        const snapshot = buildNutritionTargetsCalculationsPreview({
          targetsSnapshot: targetsSettingsSnapshot,
          useDailySnapshot,
          activeDateIso: endIso,
          rangeKind: '1d',
          rangeStartDateIso: endIso,
          rangeEndDateIso: endIso,
        });

        if (!cancelled) {
          setNutritionCalculateSnapshotToday(snapshot);
        }
      } catch (error) {
        if (!cancelled) {
          setNutritionCalculateSnapshotToday(null);
        }
      }
    }

    if (userProfile) {
      loadTodayNutritionCalculateSnapshot();
    } else {
      setNutritionCalculateSnapshotToday(null);
    }

    return () => {
      cancelled = true;
    };
  }, [userProfile, activeDateIso]);

  const macros = useMemo(
    () =>
      buildAiUserMacrosFromNutritionCalculateSnapshot(
        nutritionCalculateSnapshotToday || nutritionTargets || null
      ),
    [nutritionCalculateSnapshotToday, nutritionTargets]
  );

  const vitaminCards = useMemo(
    () =>
      buildAiUserVitaminCardsFromNutritionCalculateSnapshot(
        nutritionCalculateSnapshotToday || null
      ),
    [nutritionCalculateSnapshotToday]
  );

  const mineralCards = useMemo(
    () =>
      buildAiUserMineralCardsFromNutritionCalculateSnapshot(
        nutritionCalculateSnapshotToday || null
      ),
    [nutritionCalculateSnapshotToday]
  );

  const vitMin = useMemo(() => {
    const baseCards = [...vitaminCards, ...mineralCards];

    const comparison =
      nutritionCalculateSnapshotToday?.intakeSummary?.comparisonVsTargets || null;
    if (!comparison) {
      return baseCards;
    }

    const sodiumComparison = comparison.sodium_g || null;
    if (!sodiumComparison) {
      return baseCards;
    }

    const resolveSodiumPercentOfTarget = () => {
      if (typeof sodiumComparison.percentOfTarget === 'number') {
        return sodiumComparison.percentOfTarget;
      }

      if (
        typeof sodiumComparison.target === 'number' &&
        sodiumComparison.target > 0 &&
        typeof sodiumComparison.actual === 'number'
      ) {
        return Math.round(
          (sodiumComparison.actual / sodiumComparison.target) * 100
        );
      }

      return null;
    };

    const sodiumPercentOfTarget = resolveSodiumPercentOfTarget();
    if (sodiumPercentOfTarget == null || !Number.isFinite(sodiumPercentOfTarget)) {
      return baseCards;
    }

    const classifySodiumStatus = (percent) => {
      if (percent <= 100) return 'good';
      if (percent <= 150) return 'warn';
      return 'over';
    };

    const sodiumStatus = classifySodiumStatus(sodiumPercentOfTarget);
    const sodiumDisplayPercent = `${Math.round(sodiumPercentOfTarget)}%`;

    return baseCards.map((card) => {
      const isSodiumCard =
        card?.id === 'sodium' ||
        (typeof card?.label === 'string' &&
          card.label.toLowerCase().startsWith('sodium'));

      if (!isSodiumCard) {
        return card;
      }

      return {
        ...card,
        percentOfTarget: sodiumPercentOfTarget,
        displayPercent: sodiumDisplayPercent,
        status: sodiumStatus,
      };
    });
  }, [vitaminCards, mineralCards, nutritionCalculateSnapshotToday]);

  const vmHeadlineChip = useMemo(
    () => buildAiUserVmHeadlineChip(vitaminCards, mineralCards),
    [vitaminCards, mineralCards]
  );

  const weightLossPlan = useMemo(() => {
    if (!userProfile || !nutritionCalculateSnapshotRange) {
      return null;
    }

    try {
      const dailyIntakeByDate = {};

      const targetsSettingsSnapshot = buildNutritionTargetsSettingsPreview({
        userProfile,
        dailyIntakeByDate,
      });

      const useDailySnapshot = buildUseDailyPreviewFromNnr2023({
        userProfile,
        dailyIntakeByDate,
      });

      const bmiNode = buildBmiNodeFromSnapshots({
        userTargetsSettingsSnapshot: targetsSettingsSnapshot,
        useDailySnapshot: useDailySnapshot,
        nutritionCalculateSnapshot: nutritionCalculateSnapshotRange,
      });

      if (!bmiNode || !Number.isFinite(bmiNode.value)) {
        return null;
      }

      const bmiInsight = new BmiInsight(bmiNode);
      const computedPlan = bmiInsight.computeWeightLossPlan();

      if (!computedPlan) {
        return null;
      }

      const comparisonVsTargets =
        nutritionCalculateSnapshotRange?.intakeSummary?.comparisonVsTargets || null;

      let nutrientGapsLabel = null;
      if (comparisonVsTargets) {
        const gapsSummary = buildNutrientPrioritySummary(comparisonVsTargets);
        if (gapsSummary && gapsSummary.topDeficienciesLabel) {
          nutrientGapsLabel = gapsSummary.topDeficienciesLabel;
        }
      }

      const loggedDays =
        nutritionCalculateSnapshotRange?.intakeSummary?.totalDaysLogged ?? null;
      const rangeDays =
        nutritionCalculateSnapshotRange?.rangeInfo?.daysInRange ?? null;

      return {
        hasWeightLossGoal: computedPlan.hasWeightLossGoal,
        currentWeightKilograms: computedPlan.currentWeightKilograms,
        targetWeightKilograms: computedPlan.targetWeightKilograms,
        goalKilogramsToLose: computedPlan.goalKilograms,

        bmiValue: bmiNode.value,
        bmiCategory: bmiNode.category,

        estimatedNeedsKilocalories: bmiNode.estimatedEnergyRequirementKcal,
        suggestedTargetKilocalories:
          computedPlan.recommendationTargetIntakeKilocalories,
        deficitKilocalories:
          computedPlan.recommendationDailyDeficitKilocalories,
        weeklyLossKilograms: computedPlan.preferredWeeklyRateKilograms,

        indicativeWeeks:
          computedPlan.indicativeWeeksFromRecommended ||
          computedPlan.indicativeWeeksPreferred,
        startWeightKilograms: computedPlan.currentWeightKilograms,

        nutrientGapsLabel,

        loggedDays,
        rangeDays,
      };
    } catch (error) {
      return null;
    }
  }, [userProfile, nutritionCalculateSnapshotRange]);

  const [todaysMeals, setTodaysMeals] = useState(null);

  const previousActiveDateIsoRef = useRef(activeDateIso);

  useEffect(() => {
    const previous = previousActiveDateIsoRef.current;

    if (previous && activeDateIso && previous !== activeDateIso) {
      setLatestMinimalJson(null);
      setLatestNutritionJson(null);
      setTodaysMeals(null);
      setNutritionCalculateSnapshotRange(null);
      setNutritionCalculateSnapshotToday(null);
    }

    previousActiveDateIsoRef.current = activeDateIso;
  }, [activeDateIso]);
  useEffect(() => {
    let mounted = true;

    async function loadTodaysMeals() {
      try {
        const user = auth.currentUser;
        if (!user) {
          if (mounted) setTodaysMeals(null);
          return;
        }

        const dateIso = activeDateIso || LocalDateIso.today();
        const start = LocalDateIso.startOfDay(dateIso);
        const end = LocalDateIso.endOfDayExclusive(dateIso);

        if (!start || !end) {
          if (mounted) setTodaysMeals([]);
          return;
        }

        const startTs = Timestamp.fromDate(start);
        const endTs = Timestamp.fromDate(end);

        const colRef = collection(db, 'users', user.uid, 'meals');
        const mealsQuery = query(colRef, where('loggedAt', '>=', startTs), where('loggedAt', '<', endTs));
        const snap = await getDocs(mealsQuery);

        const meals = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          meals.push({ id: docSnap.id, data });
        });

        meals.sort((a, b) => {
          const aDate = a.data?.loggedAt?.toDate ? a.data.loggedAt.toDate() : new Date(a.data?.timestampIso || 0);
          const bDate = b.data?.loggedAt?.toDate ? b.data.loggedAt.toDate() : new Date(b.data?.timestampIso || 0);
          return bDate - aDate;
        });

        if (mounted) setTodaysMeals(meals);
      } catch (err) {
        if (mounted) setTodaysMeals([]);
      }
    }

    loadTodaysMeals();
    return () => {
      mounted = false;
    };
  }, [latestNutritionJson, activeDateIso]);

  const extractItemsFromNutritionJson = useCallback((nutJson) => {
    if (!nutJson || typeof nutJson !== 'object') return [];
    const consumed = Array.isArray(nutJson.consumed) ? nutJson.consumed : [];
    const items = consumed
      .flatMap((entry) => {
        const label =
          entry && typeof entry.label === 'string'
            ? entry.label.trim()
            : '';

        if (Array.isArray(entry.items) && entry.items.length > 0) {
          return entry.items
            .map((s) => {
              const subItem = String(s).trim();
              return label ? `${subItem} (${label})` : subItem;
            })
            .filter(Boolean);
        }

        if (label) {
          if (label.includes(',')) {
            return label
              .split(/\s*,\s*/)
              .map((p) => p.trim())
              .filter(Boolean);
          }
          return [label];
        }
        return [];
      });
    return items;
  }, []);

  const consumptionLog = useMemo(() => {
    const blocks = [];

    if (latestNutritionJson && typeof latestNutritionJson === 'object') {
      const items = extractItemsFromNutritionJson(latestNutritionJson);
      const timeIso = latestNutritionJson.timestampIso || null;
      let timeDesc = 'About to log';
      if (timeIso) {
        try {
          const parsed = new Date(timeIso);
          timeDesc = `Approx. ${parsed.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })}`;
        } catch {
          // ignore parse errors
        }
      }
      blocks.push({
        title: 'About to log',
        time: timeDesc,
        items:
          items.length > 0
            ? items
            : [String(latestNutritionJson.request?.text || 'Pending item')],
        nutrients: latestNutritionJson.consolidated_nutrients || null,
        raw: latestNutritionJson,
      });
    }

    const meals = Array.isArray(todaysMeals) ? todaysMeals : [];
    for (const meal of meals) {
      const mealJson = meal.data || {};
      const items = extractItemsFromNutritionJson(mealJson);
      let timeDesc = 'Approx. unknown time';
      try {
        const loggedAt = mealJson.loggedAt;
        let dateObj = null;
        if (loggedAt && typeof loggedAt.toDate === 'function') {
          dateObj = loggedAt.toDate();
        } else if (typeof mealJson.timestampIso === 'string') {
          dateObj = new Date(mealJson.timestampIso);
        } else if (typeof mealJson.loggedAt === 'string') {
          dateObj = new Date(mealJson.loggedAt);
        }
        if (dateObj && !Number.isNaN(dateObj.getTime())) {
          timeDesc = `Approx. ${dateObj.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })}`;
        }
      } catch {
        // ignore parse errors
      }

      blocks.push({
        title: 'Logged meal',
        time: timeDesc,
        items:
          items.length > 0
            ? items
            : [String(mealJson.request?.text || 'Meal')],
        nutrients: mealJson.consolidated_nutrients || null,
        raw: mealJson,
      });
    }

    return blocks;
  }, [latestNutritionJson, todaysMeals, extractItemsFromNutritionJson]);

  const suggestedLog = useMemo(
    () => buildDynamicSuggestedLog(latestMinimalJson, latestNutritionJson),
    [latestMinimalJson, latestNutritionJson]
  );

  const adjustedNutritionJson = useMemo(() => {
    if (!latestNutritionJson) return latestNutritionJson;
    if (!mealAdjustState) return latestNutritionJson;
    return applyAdjustmentsToNutritionJson(
      latestNutritionJson,
      mealAdjustState
    );
  }, [latestNutritionJson, mealAdjustState]);

  const results = useMemo(
    () =>
      createMealCardsFromLastNutrition(
        adjustedNutritionJson || latestNutritionJson
      ),
    [adjustedNutritionJson, latestNutritionJson]
  );

  const nutritionFacts = useMemo(
    () =>
      createNutritionFactsFromLastNutrition(
        adjustedNutritionJson || latestNutritionJson
      ),
    [adjustedNutritionJson, latestNutritionJson]
  );

  const chips = useMemo(() => {
    const vitaminsSummary = [
      vitaminsChipText(aiUserVitamins, 1),
      mineralsChipText(aiUserMinerals, 1),
    ]
      .filter(Boolean)
      .join(' • ');

    const consumptionCount = consumptionLog.length || 0;
    const suggestedCount = suggestedLog.length || 0;

    return {
      vitamins: vitaminsSummary,
      consumption: `${consumptionCount} Meals Logged`,
      suggested: `${suggestedCount} Meal Times`,
    };
  }, [consumptionLog, suggestedLog]);

  const incrementPortion = useCallback((itemId) => {
    setMealAdjustState((previous) => {
      if (!previous) return previous;
      return incrementItemPortion(previous, itemId);
    });
  }, []);

  const decrementPortion = useCallback((itemId) => {
    setMealAdjustState((previous) => {
      if (!previous) return previous;
      return decrementItemPortion(previous, itemId);
    });
  }, []);

  const createMealLogEvent = ({ servings, items }) => {
    const nowIso = new Date().toISOString();
    const entityId = `mealDraft:${hash32(
      stableStringify({
        at: nowIso,
        items: items?.map((item) => item.id),
      })
    )}`;
    const basePayload = { servings, items };
    const changeHash = hash32(stableStringify(basePayload));
    const id = `evt:${uuid()}`;
    const idempotencyKey = `idelog:${changeHash}`;
    const requestId = uuid();
    const traceId = requestId;
    const visited = [];
    return {
      id,
      entityId,
      type: 'meal.log.request',
      source: serviceName,
      requestId,
      traceId,
      visited,
      hopCount: 0,
      publishedAt: null,
      idempotencyKey,
      changeHash,
      payload: basePayload,
      trigger: 'user_click',
      triggerType: 'confirm_button',
      ingress,
    };
  };

  const createTextLogEvent = ({ text, triggerType }) => {
    const nowIso = new Date().toISOString();
    const normalizedText = String(text ?? '').trim();
    const basePayload = { text: normalizedText };
    const changeHash = hash32(stableStringify(basePayload));
    const id = `evt:${uuid()}`;
    const idempotencyKey = `idtext:${changeHash}`;
    const requestId = uuid();
    const traceId = requestId;
    const entityId = `mealText:${hash32(
      stableStringify({
        at: nowIso,
        seed: normalizedText.slice(0, 64),
      })
    )}`;
    return {
      id,
      entityId,
      type: 'meal.log.text',
      source: serviceName,
      requestId,
      traceId,
      visited: [],
      hopCount: 0,
      publishedAt: null,
      idempotencyKey,
      changeHash,
      payload: basePayload,
      trigger: 'user_input',
      triggerType: triggerType || 'send_button',
      ingress,
    };
  };

  const onConfirm = async ({ servings, items }) => {
    const event = createMealLogEvent({ servings, items });
    const destination = egressAllowlist[ingress][0];
    const result = await bus.publish(event, destination, {
      maxAttempts: 3,
    });
    return result;
  };

  const onSendText = async ({ text, triggerType, minimalInputJson }) => {
    const normalizedText = String(text ?? '').trim();
    if (!normalizedText) {
      return { status: 'dropped', reason: 'empty_text' };
    }

    if (minimalInputJson) {
      setLatestMinimalJson(minimalInputJson);
    }

    const event = createTextLogEvent({
      text: normalizedText,
      triggerType,
    });
    const destination = egressAllowlist[ingress][0];

    try {
      await bus.publish(event, destination, {
        maxAttempts: 3,
      });
    } catch (error) {
      // Ignore publish failures; the nutrition request is attempted below.
    }

    const service = await ensureGeminiService();
    if (!service) {
      const failureResult = {
        accepted: false,
        reason: 'service_init_failed',
        json: null,
        region: null,
        timestampIso: new Date().toISOString(),
      };
      return failureResult;
    }

    try {
      const nutritionResult = await service.requestNutrition({
        text: normalizedText,
        minimalInputJson: minimalInputJson || undefined,
        region: userRegion,
        entityId: event.entityId,
        ingress: 'nutrition_ingress',
        egress: 'gemini_egress',
        requestId: event.requestId,
        traceId: event.traceId,
      });

      if (nutritionResult && nutritionResult.json) {
        setLatestNutritionJson(nutritionResult.json);
      }

      return nutritionResult;
    } catch (error) {
      return {
        accepted: false,
        reason: 'nutrition_call_failed',
        json: null,
        region: null,
        timestampIso: new Date().toISOString(),
      };
    }
  };

  const sectionVisibility = useSectionVisibility();

  const value = {
    serviceName,
    activeDateIso,
    uiConfig,
    sizeToPadding,
    routes,
    labels,
    macros,
    vitMin,
    vmHeadlineChip,
    consumptionLog,
    suggestedLog,
    results,
    chips,
    nutritionFacts,
    userProfile,
    nutritionTargets,
    dailyValueReference,
    weightLossPlan,
    mealAdjustState,
    incrementPortion,
    decrementPortion,
    latestMinimalJson,
    latestNutritionJson,
    adjustedNutritionJson,
    onConfirm,
    onSendText,
    ...sectionVisibility,
  };

  return (
    <DataCtx.Provider value={value}>{children}</DataCtx.Provider>
  );
}

export function useAiUserInputData() {
  const context = useContext(DataCtx);
  if (!context) {
    throw new Error(
      'useAiUserInputData must be used within AiUserInputNutritionDataProvider'
    );
  }
  return context;
}
